import React from "react";
import "./BookletManager.scss";
import { getData, manageData } from "../../requests/CRUD";
import { TRAINING, PRICING_ANALYSIS } from "../../consts/tables";
import { sortArray } from "../../utils";
import { uploadFile, removeFile, MAX_UPLOAD_BYTES } from "../../requests/fileRequests";
import { existingFileError } from "../../consts/consts";
import Alert from "../Alert";
import Icon from "../Icon";
import svgSortAsc from "../../svgs/arrow-up.svg";
import LinkComp from "../LinkComp";

const FOLDER = 'TRAINING_BOOKLET';

export default class BookletManager extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            chapters: [],
            bookletData: { cover: null, backCover: null, dividers: {} },
            assetRowId: null,
            saving: false,
            error: null,
            success: null,
            uploading: {}
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    fetchData() {
        this.setState({ loading: true });

        // Fetch the ENTIRE brokerage-continuum tree (every nesting level) in one query
        // via a LIKE match on PARENT_SLUG, then build the nested structure client-side.
        // The previous version only fetched two levels (chapters + their direct
        // subchapters), so any deeper section never showed in the manager.
        const fetchTree = getData({
            tableCode: TRAINING,
            options: {
                filter: [
                    { text: '', code: 'DELETED', type: 'null' },
                    { text: 'brokerage-continuum%', code: 'PARENT_SLUG', type: 'match' },
                ],
                filterType: 'and',
            },
        });

        // Fetch Booklet Assets from PRICING_ANALYSIS
        const fetchAssets = getData({
            tableCode: PRICING_ANALYSIS,
            options: {
                filter: [{ text: 'training-booklet', code: 'PRODUCT_TYPE', type: 'exact' }],
            },
        });

        Promise.all([fetchTree, fetchAssets]).then(([treeRes, assetRes]) => {
            let chapters = [];
            if (treeRes && treeRes.success && Array.isArray(treeRes.data)) {
                chapters = this.buildTree(treeRes.data, 'brokerage-continuum');
            }

            let bookletData = { cover: null, backCover: null, dividers: {} };
            let assetRowId = null;
            if (assetRes && assetRes.success && assetRes.data && assetRes.data.length) {
                const assetRow = assetRes.data.reduce((latest, row) => {
                    if (!latest) return row;
                    return Number(row.ARCIMEDES_ROW_ID) > Number(latest.ARCIMEDES_ROW_ID) ? row : latest;
                }, null);
                try {
                    const items = JSON.parse(assetRow.ITEMS);
                    bookletData = { ...bookletData, ...items };
                    assetRowId = assetRow.ARCIMEDES_ROW_ID;
                } catch (e) {
                    console.error("Failed to parse booklet data", e);
                }
            }

            this.setState({ chapters, bookletData, assetRowId, loading: false });
        }).catch(err => {
            this.setState({ error: "Failed to load data", loading: false });
        });
    }

    // Recursively build the section tree from the flat row list. Each node's `key` is its
    // slug path relative to brokerage-continuum — matching the existing divider keys
    // ("close", "close/close", "1st-meeting/is-proposal-warranted", ...).
    buildTree(rows, parentPath) {
        const children = rows
            .filter(r => r.PARENT_SLUG === parentPath)
            .map(r => {
                const fullPath = `${r.PARENT_SLUG}/${r.SLUG}`;
                return {
                    id: r.SLUG,
                    name: r.NAME,
                    index: parseInt(r.INDEX),
                    key: fullPath.replace(/^brokerage-continuum\//, ''),
                    children: this.buildTree(rows, fullPath),
                };
            });
        return sortArray(children, 'index');
    }

    save() {
        this.setState({ saving: true, error: null, success: null });
        const payload = {
            PRODUCT_TYPE: 'training-booklet',
            ITEMS: JSON.stringify(this.state.bookletData),
        };
        if (this.state.assetRowId) {
            payload.ARCIMEDES_ROW_ID = this.state.assetRowId;
        }

        manageData({
            tableCode: PRICING_ANALYSIS,
            data: [payload]
        }).then(res => {
            if (res.data && res.data.responses) {
                res = res.data.responses[0];
            }
            if (res && res.success) {
                const savedRow = Array.isArray(res.data) ? res.data[0] : res.data;
                this.setState({
                    saving: false,
                    success: "Saved successfully",
                    assetRowId: savedRow && savedRow.ARCIMEDES_ROW_ID
                        ? savedRow.ARCIMEDES_ROW_ID
                        : this.state.assetRowId,
                });
                setTimeout(() => this.setState({ success: null }), 3000);
            } else {
                this.setState({ saving: false, error: "Failed to save" });
            }
        });
    }

    handleUpload(file, key) {
        if (!file) return;

        if (file.size > MAX_UPLOAD_BYTES) {
            this.setState({
                error: `File is too large (max ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB).`,
            });
            return;
        }

        this.setState(prev => ({
            uploading: { ...prev.uploading, [key]: true },
            error: null,
        }));

        uploadFile({
            fileName: file.name,
            file,
            FOLDER: FOLDER
        }).then(res => {
            const response = res.data.responses[0];
            if (response && response.success) {
                const url = response.data.url;
                this.setState(prev => {
                    const newBookletData = { ...prev.bookletData };
                    if (key === 'cover' || key === 'backCover') {
                        newBookletData[key] = url;
                    } else {
                        newBookletData.dividers[key] = url;
                    }
                    return {
                        bookletData: newBookletData,
                        uploading: { ...prev.uploading, [key]: false }
                    };
                }, () => this.save());
            } else {
                const message = (response && response.message) || "Upload failed";
                this.setState(prev => ({
                    error: message,
                    uploading: { ...prev.uploading, [key]: false }
                }));
            }
        }).catch(() => {
            this.setState(prev => ({
                error: 'Upload failed. Check your connection and try again.',
                uploading: { ...prev.uploading, [key]: false },
            }));
        });
    }

    removeAsset(key) {
        this.setState(prev => {
            const newBookletData = { ...prev.bookletData };
            if (key === 'cover' || key === 'backCover') {
                newBookletData[key] = null;
            } else {
                delete newBookletData.dividers[key];
            }
            return { bookletData: newBookletData };
        }, () => this.save());
    }

    renderAsset(key, label) {
        const url = (key === 'cover' || key === 'backCover')
            ? this.state.bookletData[key]
            : this.state.bookletData.dividers[key];
        const uploading = this.state.uploading[key];

        return (
            <div className="booklet-asset-row" key={key}>
                <div className="booklet-asset-info">
                    <div className="booklet-asset-label">{label}</div>
                    {url && (
                        <div className="booklet-asset-preview">
                            <a href={url} target="_blank" rel="noopener noreferrer">View</a>
                            <button className="button-new negative mini" onClick={() => this.removeAsset(key)}>Remove</button>
                        </div>
                    )}
                </div>
                <div className="booklet-asset-action">
                    <input 
                        type="file" 
                        accept="application/pdf,image/*"
                        onChange={(e) => this.handleUpload(e.target.files[0], key)}
                        disabled={uploading}
                    />
                    {uploading && <span className="uploading-text">Uploading...</span>}
                </div>
            </div>
        );
    }

    // Renders a section's divider row plus, recursively, all of its child sections at any
    // depth — so deep sub-sections (e.g. under 1st Meeting, Cold Call, Marketing, ...) show.
    renderNode(node, depth) {
        const label = depth === 0 ? `Chapter: ${node.name}`
            : depth === 1 ? `Subchapter: ${node.name}`
            : node.name;
        return (
            <div key={node.key} className="booklet-chapter-group">
                {this.renderAsset(node.key, label)}
                {node.children.length > 0 && (
                    <div className="booklet-subchapters">
                        {node.children.map(child => this.renderNode(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { loading, chapters, error, success, saving } = this.state;

        if (loading) return <div className="BookletManager_loading">Loading...</div>;

        return (
            <div className="BookletManager">
                <div className="BookletManager_header">
                    <div className='page-back-button' onClick={() => history.back()}>
                        <Icon svg={svgSortAsc}/>
                        Back to Training
                    </div>
                    <h1 className="BookletManager_title">Manage Training Booklet</h1>
                    <div className="BookletManager_actions">
                        {saving && <span>Saving...</span>}
                        {success && <Alert type="success">{success}</Alert>}
                        {error && <Alert type="error">{error}</Alert>}
                    </div>
                </div>

                <div className="BookletManager_content">
                    <section className="booklet-section">
                        <h2>Summit Brokerage Continuum</h2>
                        {this.renderAsset('cover', 'Front Cover')}
                        {this.renderAsset('backCover', 'Back Cover')}
                    </section>

                    <section className="booklet-section">
                        <h2>Chapter Dividers (Brokerage Continuum)</h2>
                        {chapters.map(ch => this.renderNode(ch, 0))}
                    </section>
                </div>
            </div>
        );
    }
}
